<template>
  <div>
    <Head title="Inventarios" />

    <!-- 1. Header del Módulo -->
    <InventoryHeader
      :title="'Inventarios'"
      :total-count="headerCount"
      :export-url="exportUrl"
      :report-label="reportLabel"
      @open-import-modal="showImportModal = true"
      @set-report-type="setReportType"
    />

    <!-- 2. Navegación por Pestañas: Existencias por Depósito vs Lotes y Entradas -->
    <InventoryTabs
      :model-value="activeTab"
      :stock-count="stockStats?.total_products"
      :batch-count="batchStats?.total_inventories || stats?.total_inventories"
      @update:model-value="changeTab"
    />

    <!-- 3. Vista Pestaña: Existencias por Depósito -->
    <div v-if="activeTab === 'stock'">
      <InventoryStockTab
        :deposits="deposits"
        :categories="categories"
        :products="stockProducts"
        :stats="stockStats"
        :filters="stockFilters"
      />
    </div>

    <!-- 4. Vista Pestaña: Lotes y Entradas de Inventario -->
    <div v-else class="space-y-6">
      <!-- KPIs Bento Grid Canónico de Lotes (4 Tarjetas) -->
      <InventoryStats :stats="batchStats || stats" />

      <!-- Barra de Filtros y Búsqueda de Lotes -->
      <InventoryFilters
        :filters="form"
        @filter="applyFilters"
        @reset="resetFilters"
      />

      <!-- Tabla y Vista Móvil Híbrida de Lotes -->
      <InventoryTable
        :inventories="inventories"
        :has-active-filters="hasActiveFilters"
        @annul-inventory="annulInventory"
        @reset-filters="resetFilters"
      />
    </div>

    <!-- 5. Modal Importar Excel -->
    <InventoryImportModal
      :is-open="showImportModal"
      @close="showImportModal = false"
      @submit-file="submitImport"
    />
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import pickBy from 'lodash/pickBy'
import Swal from 'sweetalert2'

import InventoryHeader from './Partials/InventoryHeader'
import InventoryTabs from './Partials/InventoryTabs'
import InventoryStockTab from './Partials/InventoryStockTab'
import InventoryStats from './Partials/InventoryStats'
import InventoryFilters from './Partials/InventoryFilters'
import InventoryTable from './Partials/InventoryTable'
import InventoryImportModal from './Partials/InventoryImportModal'

export default {
  name: 'InventoriesIndex',

  components: {
    Head,
    InventoryHeader,
    InventoryTabs,
    InventoryStockTab,
    InventoryStats,
    InventoryFilters,
    InventoryTable,
    InventoryImportModal,
  },

  layout: Layout,

  props: {
    currentTab: {
      type: String,
      default: 'stock',
    },
    deposits: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    stockProducts: {
      type: Object,
      default: () => ({ data: [], links: [] }),
    },
    stockStats: {
      type: Object,
      default: () => ({
        total_products: 0,
        total_units: 0,
        total_cost_value: 0,
        total_sale_value: 0,
        potential_margin: 0,
      }),
    },
    stockFilters: {
      type: Object,
      default: () => ({}),
    },
    batchStats: {
      type: Object,
      default: null,
    },
    stats: {
      type: Object,
      default: () => ({
        total_inventories: 0,
        total_units: 0,
        pending_requests: 0,
        processed_count: 0,
        this_month_count: 0,
        this_month_units: 0,
      }),
    },
    inventories: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    user: Object,
  },

  data() {
    return {
      activeTab: this.currentTab || 'stock',
      form: {
        search: this.filters?.search || '',
        status: this.filters?.status || null,
        date_from: this.filters?.date_from || '',
        date_to: this.filters?.date_to || '',
        report_type: this.filters?.report_type || null,
      },
      showImportModal: false,
    }
  },

  computed: {
    headerCount() {
      if (this.activeTab === 'stock') {
        return this.stockStats?.total_products ?? 0
      }
      return this.batchStats?.total_inventories ?? this.stats?.total_inventories ?? 0
    },

    exportUrl() {
      const params = new URLSearchParams(pickBy(this.form)).toString()
      return `/inventories/export${params ? '?' + params : ''}`
    },

    reportLabel() {
      if (this.form.report_type === 'daily') return 'Reporte Diario'
      if (this.form.report_type === 'custom') return 'Por Rango'
      return 'Exportar / Reportes'
    },

    hasActiveFilters() {
      return !!(this.form.search || this.form.status || this.form.date_from || this.form.date_to || this.form.report_type)
    },
  },

  watch: {
    currentTab(newVal) {
      if (newVal) {
        this.activeTab = newVal
      }
    },
  },

  methods: {
    changeTab(tab) {
      this.activeTab = tab
      this.$inertia.get('/inventories', { tab }, {
        preserveState: true,
        preserveScroll: true,
        replace: true,
      })
    },

    applyFilters(newFilters) {
      this.form = { ...this.form, ...newFilters }
      this.$inertia.get('/inventories', pickBy(this.form), {
        preserveState: true,
        replace: true,
      })
    },

    resetFilters() {
      this.form = {
        search: '',
        status: null,
        date_from: '',
        date_to: '',
        report_type: null,
      }
      this.$inertia.get('/inventories', {}, {
        preserveState: true,
        replace: true,
      })
    },

    setReportType(type) {
      this.form.report_type = type
      if (type === 'daily') {
        const today = new Date().toISOString().split('T')[0]
        this.form.date_from = today
        this.form.date_to = today
      }
      this.applyFilters(this.form)
    },

    annulInventory(inventory) {
      Swal.fire({
        title: `¿Anular solicitud #${inventory.id}?`,
        text: 'Esta acción anulará la solicitud de inventario pendiente.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Sí, anular',
        cancelButtonText: 'Cancelar',
      }).then(res => {
        if (res.isConfirmed) {
          this.$inertia.post(`/inventories/${inventory.id}/annul`, {}, {
            preserveScroll: true,
            onSuccess: () => {
              Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: 'Solicitud anulada',
                showConfirmButton: false,
                timer: 1500,
              })
            },
          })
        }
      })
    },

    submitImport(file) {
      const formData = new FormData()
      formData.append('file', file)

      this.$inertia.post('/inventories/import', formData, {
        onSuccess: () => {
          this.showImportModal = false
          Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: 'Inventario importado correctamente',
            showConfirmButton: false,
            timer: 2000,
          })
        },
      })
    },
  },
}
</script>
